// ---------------------------------------------------------------------------
// Agent Debug Dashboard — SIGS Parser
// Handles all 4 input formats and 3 ReferencedDocuments schemas
// ---------------------------------------------------------------------------

import type {
  AgentType,
  AgentDebugData,
  Citation,
  EnvironmentContext,
  EvaluationMetric,
  ExecutionStatus,
  GroundingDataPayload,
  ParseResult,
  ParsedVariant,
  ScoreClass,
  SkillExecutionDetail,
  TeamsAgentDebugTrajectoryEntry,
  TeamsAgentDebugTrajectoryMessage,
  TeamsAgentDebugTrajectoryPayload,
} from '../types/debug';

import { getFriendlyAgentName, isTeamsAgentSignal } from './agentNameUtils.js';
import { convertOcvToSig, isOcvPayload } from './ocvToSigConverter.js';

const MAX_INPUT_SIZE = 10 * 1024 * 1024; // 10 MB

// Known binary metrics (0/1 scale)
const BINARY_METRICS = new Set([
  'ToolSelectionCorrectnessBinary',
  'ToolSelectionCorrectnessBinaryV2',
  'ToolSelectionCorrectnessV2',
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Treat null, "null", "undefined", "", and undefined as absent */
function isNullish(value: unknown): value is null | undefined {
  return (
    value == null || value === 'null' || value === 'undefined' || value === ''
  );
}

/** Strip BOM, markdown fences, trim */
function preProcess(input: string): string {
  let s = input.replace(/^\uFEFF/, '');
  s = s.replace(/^```(?:json)?\s*\n?/g, '').replace(/\n?```\s*$/g, '');
  return s.trim();
}

// ---------------------------------------------------------------------------
// Agent type detection
// ---------------------------------------------------------------------------

function detectAgentType(appScenario: string | undefined): AgentType {
  if (!appScenario) return 'Unknown';
  if (appScenario.includes('TeamsOrchestrator')) return 'PA';
  if (appScenario.includes('MeetingOrchestrator')) return 'MA';
  return 'Unknown';
}

// ---------------------------------------------------------------------------
// Execution status normalization
// ---------------------------------------------------------------------------

function normalizeExecutionStatus(
  raw: Record<string, unknown> | undefined | null,
): ExecutionStatus {
  if (!raw) {
    return { LLMCompletionStatus: 'Unknown', SkillResponseStatus: 'Unknown' };
  }
  return {
    // TRANSITION: Remove once MA orchestrator emits canonical names
    LLMCompletionStatus:
      (raw.LLMCompletionStatus as string | undefined) ??
      (raw.completionStatus as string | undefined) ??
      'Unknown',
    // TRANSITION: Remove once MA orchestrator emits canonical names
    SkillResponseStatus:
      (raw.SkillResponseStatus as string | undefined) ??
      (raw.category as string | undefined) ??
      'Unknown',
  };
}

// ---------------------------------------------------------------------------
// Skill normalization
// ---------------------------------------------------------------------------

function normalizeSkillDetails(
  refDocs: Record<string, unknown>,
): SkillExecutionDetail[] {
  const raw =
    (refDocs.skillExecutionDetails as unknown[] | undefined) ??
    refDocs.skillExecution;
  if (!raw) return [];

  const items: unknown[] = Array.isArray(raw) ? raw : [raw];

  return items.map((item) => {
    const obj = item as Record<string, unknown>;
    // TRANSITION: Remove once MA orchestrator emits canonical names
    const statusRaw =
      (obj.executionStatus as Record<string, unknown> | undefined) ??
      (obj.responseStatus as Record<string, unknown> | undefined);

    const detail: SkillExecutionDetail = {
      // TRANSITION: Remove once MA orchestrator emits canonical names
      executedSkillId:
        (obj.executedSkillId as string | undefined) ??
        (obj.invokedSkill as string | undefined) ??
        'Unknown',
      executionStatus: normalizeExecutionStatus(statusRaw),
    };

    if (obj.parameters != null && Array.isArray(obj.parameters)) {
      detail.parameters = obj.parameters as (string | null)[];
    }
    if (typeof obj.response === 'string') {
      detail.response = obj.response;
    }
    if (obj.suggestedAction != null) {
      detail.suggestedAction = obj.suggestedAction;
    }
    if (obj.citations != null && Array.isArray(obj.citations)) {
      detail.citations = obj.citations as Citation[];
    }
    if (typeof obj.detectedIntent === 'string') {
      detail.detectedIntent = obj.detectedIntent;
    }
    if (typeof obj.agentType === 'string') {
      detail.agentType = obj.agentType;
    }

    return detail;
  });
}

// ---------------------------------------------------------------------------
// JSON repair — close open strings/brackets for mid-string truncated JSON
// ---------------------------------------------------------------------------

export function repairTruncatedJson(str: string): string {
  const stack: ('{' | '[')[] = [];
  let inString = false;
  let escaped = false;

  for (const ch of str) {
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === '\\' && inString) {
      escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (!inString) {
      if (ch === '{' || ch === '[') stack.push(ch);
      else if (ch === '}' || ch === ']') stack.pop();
    }
  }

  let result = str;
  // Close the open string if truncated mid-value
  if (inString) result += '"';
  // Remove a trailing comma before closing (invalid JSON)
  const trimmed = result.trimEnd();
  if (trimmed.endsWith(',')) result = trimmed.slice(0, -1);
  // Close all open brackets in reverse order
  for (let i = stack.length - 1; i >= 0; i--) {
    result += stack[i] === '{' ? '}' : ']';
  }
  return result;
}

// ---------------------------------------------------------------------------
// Grounding data source payload detection + parsing
// ---------------------------------------------------------------------------

function parseGroundingDataPayload(
  parsed: Record<string, unknown>,
): GroundingDataPayload | undefined {
  const rawSources = parsed['data sources'];
  if (!Array.isArray(rawSources)) return undefined;
  return {
    userIntent: (parsed['user intent'] as string) ?? '',
    currentThread: (parsed['current thread'] as string) ?? '',
    dataSources: rawSources.map((ds) => {
      const dsObj = ds as Record<string, unknown>;
      return {
        sourceName: (dsObj['source name'] as string) ?? '',
        executionStatement: (dsObj['Execution statement'] as string) ?? '',
        results: Array.isArray(dsObj.result)
          ? (dsObj.result as Record<string, unknown>[]).map((r) => ({
              Idx:
                typeof r.Idx === 'number'
                  ? r.Idx
                  : parseInt(String(r.Idx), 10) || 0,
              query: (r.query as string) ?? '',
              response: (r.response as string) ?? '',
            }))
          : [],
      };
    }),
  };
}

function parseTeamsAgentDebugTrajectoryMessage(
  raw: unknown,
): TeamsAgentDebugTrajectoryMessage | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const message = raw as Record<string, unknown>;
  return {
    role: typeof message.role === 'string' ? message.role : '',
    content: message.content,
    usage:
      message.usage !== null &&
      typeof message.usage === 'object' &&
      !Array.isArray(message.usage)
        ? (message.usage as Record<string, unknown>)
        : undefined,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function stringifyTrajectoryValue(value: unknown): string {
  if (typeof value === 'string') return value;
  const serialized = JSON.stringify(value, null, 2);
  return serialized ?? String(value);
}

function getTrajectoryContentBlocks(
  entry: TeamsAgentDebugTrajectoryEntry,
): unknown[] {
  if (entry.message) {
    return Array.isArray(entry.message.content)
      ? entry.message.content
      : [entry.message.content];
  }
  if (entry.type === 'tool_result') {
    return [
      {
        type: 'tool_result',
        tool_use_id: entry.toolUseId,
        is_error: entry.isError,
        content: entry.content,
      },
    ];
  }
  return [];
}

function extractTrajectorySkillDetails(
  entries: TeamsAgentDebugTrajectoryEntry[],
): SkillExecutionDetail[] {
  const resultsByCallId = new Map<
    string,
    { content: unknown; isError: boolean }
  >();

  for (const entry of entries) {
    for (const block of getTrajectoryContentBlocks(entry)) {
      if (block === null || typeof block !== 'object' || Array.isArray(block)) {
        continue;
      }
      const record = block as Record<string, unknown>;
      if (
        record.type === 'tool_result' &&
        typeof record.tool_use_id === 'string'
      ) {
        resultsByCallId.set(record.tool_use_id, {
          content: record.content,
          isError: record.is_error === true,
        });
      }
    }
  }

  const details: SkillExecutionDetail[] = [];
  for (const entry of entries) {
    for (const block of getTrajectoryContentBlocks(entry)) {
      if (block === null || typeof block !== 'object' || Array.isArray(block)) {
        continue;
      }
      const record = block as Record<string, unknown>;
      if (record.type !== 'tool_use') continue;

      const callId = typeof record.id === 'string' ? record.id : '';
      const result = callId ? resultsByCallId.get(callId) : undefined;
      const status = result
        ? result.isError
          ? 'Failure'
          : 'Success'
        : 'Unknown';
      details.push({
        executedSkillId:
          typeof record.name === 'string' ? record.name : 'Unknown',
        executionStatus: {
          LLMCompletionStatus: status,
          SkillResponseStatus: status,
        },
        parameters:
          record.input === undefined
            ? undefined
            : [stringifyTrajectoryValue(record.input)],
        response:
          result === undefined
            ? undefined
            : stringifyTrajectoryValue(result.content),
      });
    }
  }

  return details;
}

function normalizeTrajectoryContentItem(item: unknown): unknown {
  if (!isRecord(item)) return item;

  if (item.type === 'tool_use') {
    const { args, ...rest } = item;
    return {
      ...rest,
      input: args,
    };
  }

  if (item.type === 'tool_result') {
    const { toolUseId, isError, ...rest } = item;
    return {
      ...rest,
      tool_use_id: toolUseId,
      is_error: isError,
    };
  }

  return item;
}

function normalizeTrajectoryUsage(
  usage: unknown,
): Record<string, unknown> | undefined {
  if (!isRecord(usage)) return undefined;

  return {
    output_tokens: usage.outputTokens,
    input_tokens: usage.inputTokens,
    cache_read_input_tokens: usage.cacheReadInputTokens,
    cache_creation_input_tokens: usage.cacheCreationInputTokens,
  };
}

function parseExecutionTrajectoryPayload(
  parsed: Record<string, unknown>,
): TeamsAgentDebugTrajectoryPayload | undefined {
  if (
    typeof parsed.schemaVersion !== 'string' ||
    !Array.isArray(parsed.messages)
  ) {
    return undefined;
  }

  const sessionId =
    typeof parsed.sessionId === 'string' ? parsed.sessionId : '';
  const messages = parsed.messages.filter(
    (message): message is Record<string, unknown> =>
      isRecord(message) && message.type === 'message',
  );
  // Without a complete sequence, preserve the producer's array order rather
  // than guessing positions for partially numbered messages.
  if (
    messages.every(
      (message): message is Record<string, unknown> & { sequence: number } =>
        typeof message.sequence === 'number' &&
        Number.isFinite(message.sequence),
    )
  ) {
    messages.sort((left, right) => left.sequence - right.sequence);
  }
  const entries: TeamsAgentDebugTrajectoryEntry[] = messages.map((message) => {
    const usage = normalizeTrajectoryUsage(message.usage);
    return {
      type: typeof message.role === 'string' ? message.role : 'unknown',
      subtype: '',
      sessionId,
      timestamp: typeof message.timestamp === 'string' ? message.timestamp : '',
      message: {
        role: typeof message.role === 'string' ? message.role : '',
        content: Array.isArray(message.content)
          ? message.content.map(normalizeTrajectoryContentItem)
          : message.content,
        usage,
      },
      syntheticCumulativeUsage:
        isRecord(message.usage) &&
        message.usage.isSyntheticCumulativeUsage === true,
    };
  });

  return {
    conversationId:
      typeof parsed.sessionId === 'string' ? parsed.sessionId : '',
    jsonlFiles: [],
    entryCount: entries.length,
    entries,
    source: typeof parsed.provider === 'string' ? parsed.provider : '',
  };
}

function parseTeamsAgentDebugTrajectoryPayload(
  parsed: Record<string, unknown>,
): TeamsAgentDebugTrajectoryPayload | undefined {
  const trajectoryPayload = parseExecutionTrajectoryPayload(parsed);
  if (trajectoryPayload) return trajectoryPayload;
  if (!Array.isArray(parsed.entries)) return undefined;

  const entries: TeamsAgentDebugTrajectoryEntry[] = parsed.entries
    .filter(
      (entry): entry is Record<string, unknown> =>
        entry !== null && typeof entry === 'object' && !Array.isArray(entry),
    )
    .map((entry) => ({
      type: typeof entry.type === 'string' ? entry.type : 'unknown',
      subtype: typeof entry.subtype === 'string' ? entry.subtype : '',
      sessionId: typeof entry.session_id === 'string' ? entry.session_id : '',
      timestamp: typeof entry.timestamp === 'string' ? entry.timestamp : '',
      message: parseTeamsAgentDebugTrajectoryMessage(entry.message),
      toolUseId:
        typeof entry.tool_use_id === 'string' ? entry.tool_use_id : undefined,
      isError: typeof entry.is_error === 'boolean' ? entry.is_error : undefined,
      content: entry.content,
      syntheticCumulativeUsage: entry._synthetic_cumulative_usage === true,
    }));

  const jsonlFiles = Array.isArray(parsed.jsonl_files)
    ? parsed.jsonl_files
        .filter(
          (file): file is Record<string, unknown> =>
            file !== null && typeof file === 'object' && !Array.isArray(file),
        )
        .map((file) => ({
          path: typeof file.path === 'string' ? file.path : '',
          sizeBytes: typeof file.size_bytes === 'number' ? file.size_bytes : 0,
        }))
    : [];

  return {
    conversationId:
      typeof parsed.conversation_id === 'string' ? parsed.conversation_id : '',
    jsonlFiles,
    entryCount:
      typeof parsed.entry_count === 'number'
        ? parsed.entry_count
        : entries.length,
    entries,
    source: typeof parsed.source === 'string' ? parsed.source : '',
  };
}

export type TrajectoryParseResult =
  | { ok: true; data: TeamsAgentDebugTrajectoryPayload }
  | { ok: false; error: string };

const TRAJECTORY_WRAPPER_KEYS = [
  'ExecutionTrajectory',
  'executionTrajectory',
  'ReferencedDocuments',
  'referencedDocuments',
];

function toRecord(value: unknown): Record<string, unknown> | undefined {
  if (isRecord(value)) return value;
  if (typeof value !== 'string') return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Parses a standalone ExecutionTrajectory JSON (or the legacy `entries`
 * trajectory shape) into the payload rendered by the Trajectory tab. Also
 * accepts the trajectory nested under a single wrapper key such as
 * `ExecutionTrajectory` or `ReferencedDocuments`.
 */
export function parseTrajectoryInput(input: string): TrajectoryParseResult {
  const root = toRecord(input.trim());
  if (!root) {
    return { ok: false, error: 'Input is not a valid JSON object.' };
  }

  const candidates = [
    root,
    ...TRAJECTORY_WRAPPER_KEYS.map((key) => toRecord(root[key])),
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const payload = parseTeamsAgentDebugTrajectoryPayload(candidate);
    if (payload) {
      if (payload.entries.length === 0) {
        return {
          ok: false,
          error: 'The trajectory does not contain any messages.',
        };
      }
      return { ok: true, data: payload };
    }
  }

  return {
    ok: false,
    error:
      'Not a recognized ExecutionTrajectory. Expected an object with "schemaVersion" and "messages".',
  };
}

// ---------------------------------------------------------------------------
// ReferencedDocuments extraction
// ---------------------------------------------------------------------------

interface RefDocsResult {
  enabledSkills: string[];
  skillExecutionDetails: SkillExecutionDetail[];
  transcript: string;
  conversationHistory: string;
  wasTruncated: boolean;
  groundingDataPayload?: GroundingDataPayload;
  debugTrajectory?: TeamsAgentDebugTrajectoryPayload;
}

function extractReferencedDocuments(
  events: Record<string, unknown>[],
): RefDocsResult {
  const result: RefDocsResult = {
    enabledSkills: [],
    skillExecutionDetails: [],
    transcript: '',
    conversationHistory: '',
    wasTruncated: false,
  };

  // Step 1: Check AugloopContextEvent (MA Phase 2 — native object)
  const augloopEvent = events.find(
    (e) => e.EventType === 'AugloopContextExecutionEvent',
  );
  if (augloopEvent) {
    const refDocs = augloopEvent.ReferencedDocuments;
    if (refDocs != null && !isNullish(refDocs)) {
      // May be an object or a stringified JSON string
      let rd: Record<string, unknown> | null = null;
      if (typeof refDocs === 'object') {
        rd = refDocs as Record<string, unknown>;
      } else if (typeof refDocs === 'string') {
        try {
          rd = JSON.parse(refDocs) as Record<string, unknown>;
        } catch {
          // ignore unparseable string
        }
      }
      if (rd) {
        const groundingPayload = parseGroundingDataPayload(rd);
        if (groundingPayload) {
          result.groundingDataPayload = groundingPayload;
        } else {
          result.debugTrajectory = parseTeamsAgentDebugTrajectoryPayload(rd);
          if (Array.isArray(rd.enabledSkills)) {
            result.enabledSkills = rd.enabledSkills as string[];
          }
          result.skillExecutionDetails = normalizeSkillDetails(rd);
          if (typeof rd.transcript === 'string') {
            result.transcript = rd.transcript;
          }
          if (typeof rd.conversationHistory === 'string') {
            result.conversationHistory = rd.conversationHistory;
          }
        }
      }
    }
  }

  // Step 2: Check CopilotChatEvent (PA + MA Pre-Phase 2 — stringified JSON)
  const chatEvent = events.find(
    (e) => e.EventType === 'CopilotChatExecutionEvent',
  );
  if (chatEvent) {
    const rawRef = chatEvent.ReferencedDocuments;
    if (isRecord(rawRef)) {
      const groundingPayload = parseGroundingDataPayload(rawRef);
      if (groundingPayload) {
        result.groundingDataPayload = groundingPayload;
      } else {
        const debugTrajectoryPayload =
          parseTeamsAgentDebugTrajectoryPayload(rawRef);
        if (
          debugTrajectoryPayload &&
          debugTrajectoryPayload.entries.length > 0
        ) {
          result.debugTrajectory = debugTrajectoryPayload;
        }
        if (result.skillExecutionDetails.length === 0) {
          result.skillExecutionDetails = normalizeSkillDetails(rawRef);
        }
        if (
          result.enabledSkills.length === 0 &&
          Array.isArray(rawRef.enabledSkills)
        ) {
          result.enabledSkills = rawRef.enabledSkills as string[];
        }
        if (!result.transcript && typeof rawRef.transcript === 'string') {
          result.transcript = rawRef.transcript;
        }
        if (
          !result.conversationHistory &&
          typeof rawRef.conversationHistory === 'string'
        ) {
          result.conversationHistory = rawRef.conversationHistory;
        }
      }
    } else if (typeof rawRef === 'string' && !isNullish(rawRef)) {
      // Detect and strip <truncated> marker
      if (rawRef.endsWith('<truncated>')) {
        result.wasTruncated = true;
      }
      const cleaned = rawRef.replace(/<truncated>$/, '');
      try {
        const parsed = JSON.parse(cleaned) as Record<string, unknown>;

        // Detect grounding data source payload (has "data sources" key)
        const groundingPayload = parseGroundingDataPayload(parsed);
        if (groundingPayload) {
          result.groundingDataPayload = groundingPayload;
        } else {
          const debugTrajectoryPayload =
            parseTeamsAgentDebugTrajectoryPayload(parsed);
          if (debugTrajectoryPayload) {
            result.debugTrajectory = debugTrajectoryPayload;
          }
          // Only merge if Step 1 didn't already populate skills
          if (result.skillExecutionDetails.length === 0) {
            result.skillExecutionDetails = normalizeSkillDetails(parsed);
          }
          if (
            result.enabledSkills.length === 0 &&
            Array.isArray(parsed.enabledSkills)
          ) {
            result.enabledSkills = parsed.enabledSkills as string[];
          }
          if (!result.transcript && typeof parsed.transcript === 'string') {
            result.transcript = parsed.transcript;
          }
          if (
            !result.conversationHistory &&
            typeof parsed.conversationHistory === 'string'
          ) {
            result.conversationHistory = parsed.conversationHistory;
          }
        }
      } catch (parseErr) {
        // Warn if this looks like grounding data that failed to parse
        if (
          cleaned.includes('"data sources"') ||
          cleaned.includes('"user intent"')
        ) {
          console.warn(
            '[sigs] ReferencedDocuments looks like grounding data but JSON.parse failed:',
            parseErr instanceof Error ? parseErr.message : String(parseErr),
            '\nFirst 300 chars:',
            cleaned.slice(0, 300),
          );
        }
        // Try to repair truncated JSON (closes open strings + brackets)
        try {
          const fixed = repairTruncatedJson(cleaned);
          const parsed = JSON.parse(fixed) as Record<string, unknown>;
          const groundingPayload = parseGroundingDataPayload(parsed);
          if (groundingPayload) {
            result.groundingDataPayload = groundingPayload;
          } else {
            const debugTrajectoryPayload =
              parseTeamsAgentDebugTrajectoryPayload(parsed);
            if (debugTrajectoryPayload) {
              result.debugTrajectory = debugTrajectoryPayload;
            }
            if (result.skillExecutionDetails.length === 0) {
              result.skillExecutionDetails = normalizeSkillDetails(parsed);
            }
            if (
              result.enabledSkills.length === 0 &&
              Array.isArray(parsed.enabledSkills)
            ) {
              result.enabledSkills = parsed.enabledSkills as string[];
            }
          }
        } catch {
          // Silently ignore — ReferencedDocuments couldn't be parsed
        }
      }
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Evaluation metrics parsing
// ---------------------------------------------------------------------------

function parseEvalMetrics(raw: unknown): EvaluationMetric[] {
  if (isNullish(raw)) return [];
  const str = typeof raw === 'string' ? raw : '';
  if (!str) return [];
  try {
    const parsed: unknown = JSON.parse(str);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((m: Record<string, unknown>) => ({
      evaluationName: (m.evaluationName as string) ?? 'Unknown',
      score:
        typeof m.score === 'number'
          ? m.score
          : parseFloat(String(m.score)) || -1,
      explanation: (m.explanation as string) ?? '',
    }));
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Variants parsing
// ---------------------------------------------------------------------------

function parseVariant(raw: string): ParsedVariant {
  const colonIdx = raw.indexOf(':');
  if (colonIdx === -1) {
    const key = raw.trim();
    return {
      key,
      value: '',
      raw,
      isAugLoopFlag: isAugLoopFlag(key),
    };
  }
  const key = raw.slice(0, colonIdx).trim();
  const value = raw.slice(colonIdx + 1).trim();
  return {
    key,
    value,
    raw,
    isAugLoopFlag: isAugLoopFlag(key),
  };
}

function isAugLoopFlag(key: string): boolean {
  const lower = key.toLowerCase();
  return lower.includes('augloop') || lower.includes('augmentation');
}

// ---------------------------------------------------------------------------
// Score classification
// ---------------------------------------------------------------------------

export function classifyScore(score: number, metricName: string): ScoreClass {
  if (score === -1) return 'not-applicable';
  if (BINARY_METRICS.has(metricName)) {
    return score === 1 ? 'pass' : 'fail';
  }
  if (score === 0 || score === 1) {
    // Could be binary even if not in the known set
    return score === 1 ? 'pass' : 'fail';
  }
  if (score >= 5) return 'good';
  if (score >= 4) return 'fair';
  return 'poor';
}

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------

export function getSkillPassCount(data: AgentDebugData): {
  passed: number;
  total: number;
} {
  const total = data.skillExecutionDetails.length;
  const passed = data.skillExecutionDetails.filter(
    (s) =>
      s.executionStatus.SkillResponseStatus === 'Success' ||
      s.executionStatus.LLMCompletionStatus === 'Success',
  ).length;
  return { passed, total };
}

export function getEvalAverage(data: AgentDebugData): number | null {
  const scorable = data.evaluationMetrics.filter((m) => m.score >= 0);
  if (scorable.length === 0) return null;
  return scorable.reduce((sum, m) => sum + m.score, 0) / scorable.length;
}

/** Format metric name: insert spaces in PascalCase, title-case kebab/snake-case */
export function formatMetricName(name: string): string {
  // Replace underscores and hyphens with spaces first
  let result = name.replace(/[_-]/g, ' ');
  // Insert spaces at PascalCase boundaries
  result = result.replace(/([a-z])([A-Z])/g, '$1 $2');
  // Title-case each word
  return result
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Single signal parser
// ---------------------------------------------------------------------------

function parseSignal(signal: Record<string, unknown>): AgentDebugData {
  const customProps = (signal.CustomProperties ?? {}) as Record<
    string,
    unknown
  >;
  const hasCustomProps =
    signal.CustomProperties != null &&
    typeof signal.CustomProperties === 'object' &&
    Object.keys(signal.CustomProperties as Record<string, unknown>).length > 0;
  const compressedRaw = customProps.CompressedData;
  const hasCompressedData =
    compressedRaw != null &&
    typeof compressedRaw === 'object' &&
    Object.keys(compressedRaw as Record<string, unknown>).length > 0;
  const compressed = (compressedRaw ?? {}) as Record<string, unknown>;
  const execCtx = (compressed.ExecutionContext ?? {}) as Record<
    string,
    unknown
  >;
  const events = (compressed.ExecutionEvents ?? []) as Record<
    string,
    unknown
  >[];
  const envCtx = (compressed.EnvironmentContext ?? {}) as Record<
    string,
    unknown
  >;

  const app = (signal.Application ?? {}) as Record<string, unknown>;
  const actor = (signal.Actor ?? {}) as Record<string, unknown>;
  const device = (signal.Device ?? {}) as Record<string, unknown>;
  const item = (signal.Item ?? {}) as Record<string, unknown>;

  // Find specific events
  const augloopEvent = events.find(
    (e) => e.EventType === 'AugloopContextExecutionEvent',
  );
  const chatEvent = events.find(
    (e) => e.EventType === 'CopilotChatExecutionEvent',
  );
  const evalEvent = events.find(
    (e) => e.EventType === 'EvaluationMetricsExecutionEvent',
  );

  const appScenario = execCtx.AppScenario as string | undefined;
  const productEntryPoint = execCtx.ProductEntryPoint as string | undefined;
  const clientAppName = execCtx.ClientAppName as string | undefined;
  const agentType = detectAgentType(appScenario);
  const isTeamsAgent = isTeamsAgentSignal(appScenario, clientAppName);
  const friendlyAgentName =
    getFriendlyAgentName(appScenario, productEntryPoint, clientAppName) ??
    agentType;

  // Extract referenced documents
  const refDocs = extractReferencedDocuments(events);
  const trajectorySkillDetails = refDocs.debugTrajectory
    ? extractTrajectorySkillDetails(refDocs.debugTrajectory.entries)
    : [];

  // Parse variants
  const rawVariants = (execCtx.Variants ?? []) as string[];
  const variants = rawVariants.map(parseVariant);

  // Parse eval metrics
  const evalMetrics = parseEvalMetrics(evalEvent?.EvaluationMetrics);

  // Environment context
  const environment: EnvironmentContext = {
    sourceBranch: (envCtx.SourceBranch as string) ?? '',
    deployRing: (envCtx.DeployRing as string) ?? '',
    region: (envCtx.Region as string) ?? '',
    roleInstance: (envCtx.RoleInstance as string) ?? '',
    cosmicAppName: (envCtx.CosmicAppName as string) ?? '',
    cosmicLocation: (envCtx.CosmicLocation as string) ?? '',
    cosmicPartition: (envCtx.CosmicPartition as string) ?? '',
  };

  return {
    // Envelope
    id: (signal.Id as string) ?? '',
    signalType: (signal.SignalType as string) ?? '',
    cv: (signal.CV as string) ?? '',
    creationTime: (signal.CreationTime as string) ?? '',
    startTime: (signal.StartTime as string) ?? '',
    endTime: (signal.EndTime as string) ?? '',
    status: (signal.Status as string) ?? '',

    // Application
    appId: (app.AadAppId as string) ?? '',
    appName: (app.AppName as string) ?? '',
    appVersion: (app.AppVer as string) ?? '',
    workload: (app.Workload as string) ?? '',

    // Actor
    tenantId: (actor.AadTenantId as string) ?? '',
    actorId: (actor.ActorId as string) ?? '',
    actorType: (actor.ActorType as string) ?? '',

    // Device
    userAgent: (device.UserAgent as string) ?? '',

    // Item
    itemId: (item.ItemId as string) ?? '',

    // Execution Context
    appScenario: appScenario ?? '',
    agentType,
    friendlyAgentName,
    clientSessionId: (execCtx.ClientSessionId as string) ?? '',
    clientPlatform: (execCtx.ClientPlatform as string) ?? '',
    clientAppName: clientAppName ?? '',
    correlationId: (execCtx.CorrelationId as string) ?? '',
    conversationId: (execCtx.ConversationId as string) ?? '',
    requestId: (execCtx.RequestId as string) ?? '',
    isTestRequest: (execCtx.IsTestRequest as boolean) ?? false,
    region: (execCtx.Region as string) ?? '',
    locale: (execCtx.Locale as string) ?? '',
    market: (execCtx.Market as string) ?? '',
    turnIndex: (execCtx.TurnIndex as string) ?? '',

    // Events
    serverSessionId: (augloopEvent?.ServerSessionId as string) ?? '',
    llmScenarioName: (augloopEvent?.LlmScenarioName as string) ?? '',
    llmScenarioId: (augloopEvent?.LlmScenarioId as string) ?? '',

    // User interaction
    userPrompt: (chatEvent?.UserPrompt as string) ?? '',
    response: (chatEvent?.Response as string) ?? '',

    // Referenced documents
    enabledSkills: refDocs.enabledSkills,
    skillExecutionDetails:
      trajectorySkillDetails.length > 0
        ? trajectorySkillDetails
        : refDocs.skillExecutionDetails,
    transcript: refDocs.transcript,
    conversationHistory: refDocs.conversationHistory,
    groundingDataPayload: refDocs.groundingDataPayload,
    debugTrajectory: refDocs.debugTrajectory,
    isTeamsAgent,

    // Evaluation
    evaluationMetrics: evalMetrics,

    // Variants
    variants,

    // Environment
    environment,

    // Raw
    rawSignal: signal,

    // CompressedData presence
    hasCompressedData: hasCustomProps ? hasCompressedData : true,

    // Truncation
    wasTruncated: refDocs.wasTruncated,
  };
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CV extraction (recursive search for CV key in any object)
// ---------------------------------------------------------------------------

export function extractCv(obj: unknown): string | null {
  if (!obj || typeof obj !== 'object') return null;
  const record = obj as Record<string, unknown>;
  // Check for CV key case-insensitively
  for (const [k, v] of Object.entries(record)) {
    if (k.toLowerCase() === 'cv' && typeof v === 'string' && v.length > 0) {
      return v;
    }
  }
  for (const val of Object.values(record)) {
    if (typeof val === 'object') {
      const found = extractCv(val);
      if (found) return found;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

export function parseSigsInput(input: string): ParseResult {
  // Pre-process
  if (!input?.trim()) {
    return { ok: false, error: 'Input is empty' };
  }

  if (input.length > MAX_INPUT_SIZE) {
    return {
      ok: false,
      error: 'Input too large (>10MB). Try a single signal or smaller batch.',
    };
  }

  const cleaned = preProcess(input);
  if (!cleaned) {
    return { ok: false, error: 'Input is empty' };
  }

  // Parse JSON
  let json: unknown;
  try {
    json = JSON.parse(cleaned);
  } catch (e) {
    const pos =
      e instanceof SyntaxError
        ? (/position (\d+)/i.exec(e.message)?.[1] ?? 'unknown')
        : 'unknown';
    return {
      ok: false,
      error: `Invalid JSON \u2014 check for missing brackets or trailing commas. Error at position ${pos}`,
    };
  }

  // Detect format
  let signals: Record<string, unknown>[];

  if (json != null && typeof json === 'object' && !Array.isArray(json)) {
    const obj = json as Record<string, unknown>;

    // Format 1: OCV payload (has OcvType + LlmPromptResponseSignals)
    if (isOcvPayload(obj)) {
      signals = convertOcvToSig(
        obj as unknown as Parameters<typeof convertOcvToSig>[0],
      );
    }
    // Format 2: API response { "value": [...] }
    else if ('value' in obj && Array.isArray(obj.value)) {
      signals = obj.value as Record<string, unknown>[];
    }
    // Format 3: Single signal with CustomProperties or SignalType or CV
    else if ('CustomProperties' in obj || 'SignalType' in obj || 'CV' in obj) {
      signals = [obj];
    }
    // Format 4: CompressedData only
    else if ('ExecutionContext' in obj || 'ExecutionEvents' in obj) {
      signals = [{ CustomProperties: { CompressedData: obj } }];
    } else {
      return {
        ok: false,
        error:
          "This JSON doesn't appear to be a SIGS signal. Expected: API response with 'value' array, array of signals, single signal with 'CustomProperties', or CompressedData.",
      };
    }
  } else if (Array.isArray(json)) {
    // Format 5: Array of signals
    signals = json as Record<string, unknown>[];
  } else {
    return {
      ok: false,
      error:
        "This JSON doesn't appear to be a SIGS signal. Expected: API response with 'value' array, array of signals, single signal with 'CustomProperties', or CompressedData.",
    };
  }

  if (signals.length === 0) {
    return { ok: false, error: 'Array is empty \u2014 no signals found' };
  }

  const data = signals.map(parseSignal);
  return { ok: true, data };
}
